import Head from 'next/head'
import styled from 'styled-components'

import { AccountPanel } from 'account/components/AccountPanel'
import { BillingAddressPanel } from 'account/components/BillingAddressPanel'
import { OrderList } from 'account/components/OrderList'
import { PaymentPanel } from 'account/components/PaymentPanel'
import { ShippingAddressPanel } from 'account/components/ShippingAddressPanel'
import { t } from 'localization'
import Spinner from 'shared/components/Spinner'
import { AccountQA } from 'shared/dataAttributes'
import { useUser } from 'shared/hooks/useUser'
import { colors, fonts, media, pageWidth, styles } from 'shared/lib'
import { Address } from 'shared/types/Address'
import { BillingInfo } from 'shared/types/BillingInfo'

const TP = 'account.components.MyAccount'

type MyAccountProps = {
  billingAddress: Address
  creditCards: BillingInfo[]
  shippingAddress: Address
}

export const MyAccount = ({ billingAddress, creditCards, shippingAddress }: MyAccountProps) => {
  const { currentUser } = useUser()

  return (
    <AccountPageLayout>
      <Head>
        <title>{t(`${TP}.pageTitle`, 'My Account | Flight Club')}</title>
      </Head>
      <PageBody data-testid="accountPageBody">
        <Title data-qa={AccountQA.MyAccountHeader} isTopSection={true}>
          {t(`${TP}.myAccount`, 'My Account')}
        </Title>
        {!currentUser && <Spinner showSpinner />}
        {currentUser && (
          <>
            <AccountInformation>
              <AccountPanel />
              <ShippingAddressPanel shippingAddress={shippingAddress} />
              <PaymentPanel creditCards={creditCards} />
              <BillingAddressPanel billingAddress={billingAddress} />
            </AccountInformation>
            <Title isTopSection={false} data-qa={AccountQA.MyOrdersHeader}>
              {t(`${TP}.myOrders`, 'My Orders')}
            </Title>
            <OrderList />
          </>
        )}
      </PageBody>
    </AccountPageLayout>
  )
}

const AccountPageLayout = styled.div`
  background-color: ${colors.FC2_WHITE};
  padding: 40px ${styles.mobilePageLayoutPadding};
  ${media.large`
    padding: 40px 0;
  `}
`

const PageBody = styled.div`
  ${pageWidth}
  ${media.large`
    margin: 0 auto;
  `}
`

const Title = styled.h3<{ isTopSection: boolean }>`
  ${fonts.HEADER_1}
  ${({ isTopSection }) => (isTopSection ? 'margin: 0 0 40px;' : 'margin: 40px 0;')}
`

const AccountInformation = styled.div`
  ${media.large`
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
  `}
`
